import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import Box from '@material-ui/core/Box';
import { makeStyles } from '@material-ui/core';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Divider from '@material-ui/core/Divider';

import RadioGroup from '@material-ui/core/RadioGroup';
import { RadioButton } from 'components/buttons/RadioButtons';
import Calendar from 'react-calendar';
import useApi, { USER_CREATE_WIDGET } from 'hooks/useApi';
import { useParams } from 'react-router-dom';
import * as DASHBOARD_ACTIONS from 'hooks/useDashboard/actions';
import { Message } from 'components/shared';
import {
  dateTypeOptions as dateTypeOptionsBase,
  dynamicDateOptions,
  dateMatchTypeOptions
} from '../content';

const useStyles = makeStyles({
  root: {
    flexGrow: 1
  },
  rootInput: {
    '& .MuiOutlinedInput-input': {
      padding: '15px 14px'
    }
  },
  divider: {
    width: '100%',
    marginTop: '25px'
  }
});
const dateTypeOptions = [
  ...dateTypeOptionsBase,
  { title: 'Use Dashboard Date', value: 'dashboardDate' }
];

const ThirdStep = ({
  widgetState,
  setActiveStep,
  setWhichPopup,
  dispatchDashboard,
  dispatchWidget,
  handleWidgetClose,
  panelIndex,
  dashboardTimeSeriesSetId
}) => {
  const classes = useStyles();
  const [state, sendRequest] = useApi();
  const { data: response, isLoading, isError, error } = state;
  const { id } = useParams();

  const { startDate, endDate, dateRangeTypeId, dateMatchType } = widgetState;

  const [dateTypeValue, setDateTypeValue] = useState('dynamicDate');

  const handleFormStateChange = event => {
    const targetName = event.target.name;

    dispatchWidget({
      type: 'UPDATED_WIDGET',
      payload: { [targetName]: Number(event.target.value) }
    });
  };

  const handleStartDateChange = newDate => {
    dispatchWidget({
      type: 'UPDATED_WIDGET',
      payload: { startDate: newDate }
    });
  };

  const handleEndDateChange = newDate => {
    dispatchWidget({
      type: 'UPDATED_WIDGET',
      payload: { endDate: newDate }
    });
  };

  const handleDateTypeChange = event => {
    if (event.target.value === 'customDate') {
      dispatchWidget({
        type: 'UPDATED_WIDGET',
        payload: { dateRangeTypeId: 0 }
      });
    } else if (event.target.value === 'dashboardDate') {
      dispatchWidget({
        type: 'UPDATED_WIDGET',
        payload: { dateRangeTypeId: null, startDate: null, endDate: null }
      });
    }
    setDateTypeValue(event.target.value);
  };

  const handleComplete = () => {
    const { benchmarks } = widgetState;

    const reducedBenchmarks = benchmarks
      .filter(item => !!item.benchmarkId)
      .map(benchmark => benchmark.benchmarkId);

    const updateData = {
      ...widgetState,
      dashboardId: id,
      benchmarks: reducedBenchmarks,
      panelIndex
    };

    if (!updateData.timeSeriesSetId) {
      updateData.timeSeriesSetId = dashboardTimeSeriesSetId;
    }

    const params = USER_CREATE_WIDGET({ ...updateData });

    sendRequest(params);
  };
  const handleBack = () => setActiveStep(prev => prev - 1);

  useEffect(() => {
    if (isLoading || isError) return;

    if (response) {
      dispatchDashboard({
        type: DASHBOARD_ACTIONS.ADD_WIDGET,
        payload: response
      });
      setWhichPopup();
      handleWidgetClose();
    }
  }, [dispatchDashboard, handleWidgetClose, isError, isLoading, response, setWhichPopup]);

  return (
    <>
      {isError && <Message type="error" text={error} />}
      <Grid container direction="row" justify="space-around" alignItems="flex-start" spacing={2}>
        <Grid item xs={12}>
          <h3>Choose date match type:</h3>
          <RadioGroup
            aria-label="dateMatchType"
            name="dateMatchType"
            value={dateMatchType}
            onChange={handleFormStateChange}
          >
            <Grid
              container
              direction="row"
              justify="flex-start"
              alignItems="flex-start"
              spacing={2}
            >
              {dateMatchTypeOptions &&
                dateMatchTypeOptions.map(option => (
                  <Grid item xs={12} md="auto" key={option.title}>
                    <RadioButton title={option.title} value={option.value} />
                  </Grid>
                ))}
            </Grid>
          </RadioGroup>
        </Grid>
        <Divider className={classes.divider} />
        <Grid item xs={12}>
          <h3>Choose date type:</h3>
          <RadioGroup
            aria-label="quiz"
            name="dateRangeTypeId"
            value={dateTypeValue}
            onChange={handleDateTypeChange}
          >
            <Grid
              container
              direction="row"
              justify="flex-start"
              alignItems="flex-start"
              spacing={1}
            >
              {dateTypeOptions &&
                dateTypeOptions.map(option => (
                  <Grid item xs={12} md="auto" key={option.title}>
                    <RadioButton title={option.title} value={option.value} />
                  </Grid>
                ))}
            </Grid>
          </RadioGroup>
          <Divider className={classes.divider} />
          <Grid
            item
            container
            xs={12}
            direction="row"
            justify="center"
            alignItems="flex-start"
            spacing={1}
          >
            {dateTypeValue && dateTypeValue === 'customDate' && (
              <>
                <Grid item xs={12} md={6}>
                  <h4 style={{ textAlign: 'center' }}>Start Date</h4>
                  <Calendar onChange={handleStartDateChange} value={startDate} />
                </Grid>
                <Grid item xs={12} md={6}>
                  <h4 style={{ textAlign: 'center' }}>End Date</h4>
                  <Calendar onChange={handleEndDateChange} value={endDate} />
                </Grid>
              </>
            )}
          </Grid>
          {dateTypeValue && dateTypeValue === 'dynamicDate' && (
            <Box pt={2}>
              <h4>Dynamic Date options:</h4>
              <RadioGroup
                aria-label="dateRangeTypeId"
                name="dateRangeTypeId"
                value={dateRangeTypeId}
                onChange={handleFormStateChange}
              >
                <Grid
                  container
                  direction="row"
                  justify="flex-start"
                  alignItems="flex-start"
                  spacing={1}
                >
                  {dynamicDateOptions &&
                    dynamicDateOptions.map(option => (
                      <Grid item xs={12} md={6} key={option.title}>
                        <RadioButton title={option.title} value={option.value} />
                      </Grid>
                    ))}
                </Grid>
              </RadioGroup>
            </Box>
          )}
          {dateTypeValue && dateTypeValue === 'dashboardDate' && (
            <Box pt={2}>
              <h4>
                The newly created widget will use the dashboard's date set. You can change it any
                time afterwards from the calendar menu then clicking on "Use Dashboard Dates".
              </h4>
            </Box>
          )}
        </Grid>
      </Grid>
      <Divider style={{ width: '100%', marginTop: '20px' }} />
      <Grid container direction="row" justify="space-between" alignItems="center" spacing={2}>
        <Grid item xs={4} md={4}>
          <Box display="flex" my={2} justifyContent="flex-start" mr="auto">
            <Button
              variant="outlined"
              style={{ marginRight: '8px' }}
              color="primary"
              onClick={handleBack}
            >
              Back
            </Button>
          </Box>
        </Grid>

        <Grid item xs={4} md={4}>
          <Box display="flex" my={2} justifyContent="flex-end" ml="auto">
            <Button variant="contained" color="primary" onClick={handleComplete}>
              Finish
            </Button>
          </Box>
        </Grid>
      </Grid>
    </>
  );
};
ThirdStep.propTypes = {
  widgetState: PropTypes.shape({
    endDate: PropTypes.string.isRequired,
    dateRangeTypeId: PropTypes.number.isRequired,
    startDate: PropTypes.string.isRequired,
    dateMatchType: PropTypes.number.isRequired,
    benchmarks: PropTypes.arrayOf(PropTypes.shape({}))
  }).isRequired,
  setActiveStep: PropTypes.func.isRequired,
  dispatchDashboard: PropTypes.func.isRequired,
  dispatchWidget: PropTypes.func.isRequired,
  handleWidgetClose: PropTypes.func.isRequired,
  setWhichPopup: PropTypes.func.isRequired,
  panelIndex: PropTypes.number.isRequired
};

export default ThirdStep;
